import { userStatePath } from "./userState";

export const TIMER_ANSWER_VERSION = 1 as const;
export const TIMER_USER_STATE_CONTENT_TYPE =
  "application/vnd.pivotalgroup.data.user-state+json";

export type TimerMode = "count-up" | "countdown";
export type TimerPersistenceMode = "wall-clock" | "active-session";
export type TimerStatus = "running" | "paused" | "completed";
export type TimerInitialStatus = Exclude<TimerStatus, "completed">;
export type TimerCompletionStatus =
  | "not-ready"
  | "pending"
  | "executing"
  | "executed";

export interface TimerConfig {
  mode: TimerMode;
  durationMs: number;
  persistenceMode: TimerPersistenceMode;
  initialStatus: TimerInitialStatus;
}

export interface TimerAnswerState {
  version: typeof TIMER_ANSWER_VERSION;
  configFingerprint: string;
  mode: TimerMode;
  persistenceMode: TimerPersistenceMode;
  durationMs: number;
  elapsedMs: number;
  startedAt: string | null;
  targetAt: string | null;
  lastPersistedAt: string;
  completedAt: string | null;
  status: TimerStatus;
  completionStatus: TimerCompletionStatus;
}

export interface TimerSnapshot {
  elapsedMs: number;
  remainingMs: number;
  displayMs: number;
  progress: number;
  completed: boolean;
}

export interface TimerUserStateRecord {
  dataPath: string[];
  key: string;
  valueType: "json";
  value: TimerAnswerState;
  updatedAt: string;
}

export interface TimerBlockDefinition {
  mode?: unknown;
  durationHours?: unknown;
  durationMinutes?: unknown;
  durationSeconds?: unknown;
  persistenceMode?: unknown;
  initialStatus?: unknown;
}

const MAX_DURATION_MS = Number.MAX_SAFE_INTEGER;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function timestamp(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function iso(nowMs: number): string {
  return new Date(Math.max(0, nowMs)).toISOString();
}

export function normalizeTimerDuration(durationMs: unknown): number {
  const numeric = typeof durationMs === "number" ? durationMs : Number(durationMs);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return Math.min(MAX_DURATION_MS, Math.floor(numeric));
}

export function normalizeTimerConfig(config: TimerConfig): TimerConfig {
  return {
    mode: config.mode === "count-up" ? "count-up" : "countdown",
    durationMs: normalizeTimerDuration(config.durationMs),
    persistenceMode:
      config.persistenceMode === "active-session"
        ? "active-session"
        : "wall-clock",
    initialStatus: config.initialStatus === "paused" ? "paused" : "running",
  };
}

export function timerConfigFromDefinition(
  definition: TimerBlockDefinition | null | undefined,
): TimerConfig {
  const durationMs =
    (Number(definition?.durationHours) || 0) * 3_600_000
    + (Number(definition?.durationMinutes) || 0) * 60_000
    + (Number(definition?.durationSeconds) || 0) * 1000;
  return normalizeTimerConfig({
    mode: definition?.mode === "count-up" ? "count-up" : "countdown",
    durationMs,
    persistenceMode: definition?.persistenceMode === "active-session"
      ? "active-session"
      : "wall-clock",
    initialStatus: definition?.initialStatus === "paused" ? "paused" : "running",
  });
}

export function restoreTimerStateForDefinition(
  value: unknown,
  definition: TimerBlockDefinition | null | undefined,
  nowMs = Date.now(),
): TimerAnswerState {
  return restoreTimerState(value, timerConfigFromDefinition(definition), nowMs);
}

export function createResetTimerState(
  definition: TimerBlockDefinition | null | undefined,
  nowMs = Date.now(),
): TimerAnswerState {
  return createInitialTimerState(timerConfigFromDefinition(definition), nowMs);
}

export function createTimerConfigFingerprint(config: TimerConfig): string {
  const normalized = normalizeTimerConfig(config);
  const parts = [
    `v${TIMER_ANSWER_VERSION}`,
    normalized.mode,
    normalized.persistenceMode,
    normalized.durationMs,
  ];
  if (normalized.initialStatus === "paused") {
    parts.push("paused");
  }
  return parts.join(":");
}

export function createInitialTimerState(
  config: TimerConfig,
  nowMs: number,
): TimerAnswerState {
  const normalized = normalizeTimerConfig(config);
  const completed = normalized.durationMs === 0;
  const running = !completed && normalized.initialStatus === "running";
  const now = iso(nowMs);

  return {
    version: TIMER_ANSWER_VERSION,
    configFingerprint: createTimerConfigFingerprint(normalized),
    mode: normalized.mode,
    persistenceMode: normalized.persistenceMode,
    durationMs: normalized.durationMs,
    elapsedMs: completed ? normalized.durationMs : 0,
    startedAt: running ? now : null,
    targetAt:
      running && normalized.persistenceMode === "wall-clock"
        ? iso(nowMs + normalized.durationMs)
        : null,
    lastPersistedAt: now,
    completedAt: completed ? now : null,
    status: completed ? "completed" : normalized.initialStatus,
    completionStatus: completed ? "pending" : "not-ready",
  };
}

export function normalizeTimerAnswerState(
  value: unknown,
): TimerAnswerState | null {
  if (!isRecord(value) || value.version !== TIMER_ANSWER_VERSION) return null;

  const mode = value.mode;
  const persistenceMode = value.persistenceMode;
  const status = value.status;
  const completionStatus = value.completionStatus;
  const lastPersistedAt = timestamp(value.lastPersistedAt);

  if (
    (mode !== "count-up" && mode !== "countdown") ||
    (persistenceMode !== "wall-clock" &&
      persistenceMode !== "active-session") ||
    (status !== "running" && status !== "paused" && status !== "completed") ||
    (completionStatus !== "not-ready" &&
      completionStatus !== "pending" &&
      completionStatus !== "executing" &&
      completionStatus !== "executed") ||
    typeof value.configFingerprint !== "string" ||
    lastPersistedAt === null
  ) {
    return null;
  }

  const durationMs = normalizeTimerDuration(value.durationMs);
  const elapsedValue =
    typeof value.elapsedMs === "number" ? value.elapsedMs : Number(value.elapsedMs);
  const elapsedMs = Number.isFinite(elapsedValue)
    ? Math.min(durationMs, Math.max(0, elapsedValue))
    : 0;
  const completed = status === "completed" || elapsedMs >= durationMs;

  return {
    version: TIMER_ANSWER_VERSION,
    configFingerprint: value.configFingerprint,
    mode,
    persistenceMode,
    durationMs,
    elapsedMs: completed ? durationMs : elapsedMs,
    startedAt:
      timestamp(value.startedAt) === null ? null : String(value.startedAt),
    targetAt: timestamp(value.targetAt) === null ? null : String(value.targetAt),
    lastPersistedAt: String(value.lastPersistedAt),
    completedAt:
      timestamp(value.completedAt) === null ? null : String(value.completedAt),
    status: completed ? "completed" : status,
    completionStatus: completed
      ? completionStatus === "not-ready"
        ? "pending"
        : completionStatus
      : "not-ready",
  };
}

export function calculateTimerSnapshot(
  state: TimerAnswerState,
  config: TimerConfig,
  nowMs: number,
): TimerSnapshot {
  const normalized = normalizeTimerConfig(config);
  const anchor = timestamp(state.lastPersistedAt) ?? nowMs;
  const runningDelta =
    state.status === "running" ? Math.max(0, nowMs - anchor) : 0;
  const elapsedMs = Math.min(
    normalized.durationMs,
    Math.max(0, state.elapsedMs + runningDelta),
  );
  const remainingMs = Math.max(0, normalized.durationMs - elapsedMs);
  const completed = normalized.durationMs === 0 || elapsedMs >= normalized.durationMs;

  return {
    elapsedMs,
    remainingMs,
    displayMs: normalized.mode === "count-up" ? elapsedMs : remainingMs,
    progress:
      normalized.durationMs === 0 ? 1 : elapsedMs / normalized.durationMs,
    completed,
  };
}

export function advanceTimerState(
  state: TimerAnswerState,
  config: TimerConfig,
  nowMs: number,
): TimerAnswerState {
  const snapshot = calculateTimerSnapshot(state, config, nowMs);
  const now = iso(nowMs);
  const newlyCompleted = snapshot.completed && state.status !== "completed";

  return {
    ...state,
    elapsedMs: snapshot.elapsedMs,
    lastPersistedAt: now,
    completedAt: newlyCompleted ? now : state.completedAt,
    status: snapshot.completed ? "completed" : state.status,
    completionStatus: snapshot.completed
      ? state.completionStatus === "not-ready"
        ? "pending"
        : state.completionStatus
      : "not-ready",
  };
}

export function restoreTimerState(
  value: unknown,
  config: TimerConfig,
  nowMs: number,
): TimerAnswerState {
  const normalizedConfig = normalizeTimerConfig(config);
  const restored = normalizeTimerAnswerState(value);

  if (
    !restored ||
    restored.configFingerprint !==
      createTimerConfigFingerprint(normalizedConfig)
  ) {
    return createInitialTimerState(normalizedConfig, nowMs);
  }

  if (
    restored.persistenceMode === "active-session" &&
    restored.status === "running"
  ) {
    return {
      ...restored,
      lastPersistedAt: iso(nowMs),
      targetAt: null,
    };
  }

  return advanceTimerState(restored, normalizedConfig, nowMs);
}

export function pauseTimerState(
  state: TimerAnswerState,
  config: TimerConfig,
  nowMs: number,
): TimerAnswerState {
  const advanced = advanceTimerState(state, config, nowMs);
  return advanced.status === "completed"
    ? advanced
    : { ...advanced, status: "paused", targetAt: null };
}

export function resumeTimerState(
  state: TimerAnswerState,
  config: TimerConfig,
  nowMs: number,
): TimerAnswerState {
  if (state.status === "completed") return state;
  const normalized = normalizeTimerConfig(config);
  const remainingMs = Math.max(0, normalized.durationMs - state.elapsedMs);

  return {
    ...state,
    status: "running",
    startedAt: state.startedAt ?? iso(nowMs),
    targetAt:
      normalized.persistenceMode === "wall-clock"
        ? iso(nowMs + remainingMs)
        : null,
    lastPersistedAt: iso(nowMs),
  };
}

export function timerUserStateLocator(
  dataPath: string[],
  blockId: string,
): { dataPath: string[]; key: string } {
  return { dataPath, key: timerUserStateKey(blockId) };
}

export function timerUserStateKey(blockId: string): string {
  return `timer.${blockId}`;
}

export function timerUserStatePath(
  userId: string,
  dataPath: string[],
  blockId: string,
): string {
  return userStatePath(userId, dataPath, timerUserStateKey(blockId));
}

export function buildTimerUserStateRecord(
  dataPath: string[],
  blockId: string,
  state: TimerAnswerState,
): TimerUserStateRecord {
  return {
    dataPath,
    key: timerUserStateKey(blockId),
    valueType: "json",
    value: state,
    updatedAt: state.lastPersistedAt,
  };
}

export function extractTimerUserState(record: unknown): unknown {
  if (!isRecord(record) || record.valueType !== "json") return null;
  let value = record.value;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

export function extractLegacyTimerAnswerState(answer: unknown): unknown {
  if (!isRecord(answer)) return null;
  let data = answer.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (isRecord(data) && "timer" in data) return data.timer;
  return data;
}

export function formatTimerDuration(durationMs: unknown): string {
  const totalSeconds = Math.max(
    0,
    Math.floor(normalizeTimerDuration(durationMs) / 1000),
  );
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [minutes, seconds].map((part) => String(part).padStart(2, "0"));

  return hours > 0
    ? `${String(hours).padStart(2, "0")}:${parts.join(":")}`
    : parts.join(":");
}
